import { useCallback, useEffect, useRef, useState } from "react";

import {
  useRequestWithFeedback,
  type OverrideRunProps,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { useObjectMemo } from "../../../hooks";

import { API } from "./api";

import type { WebDocument } from "./types";

export const useCreateShareDocuments = () => {
  const l10n = useLibrary("translations");
  const { send, loading } = useRequestWithFeedback<{
    detail: string;
  }>();

  const createShareDocuments = useCallback(
    (
      documents: WebDocument[],
      onSuccess?: () => void,
      overrideRunProps?: OverrideRunProps<{ detail: string }>,
    ) => {
      send({
        action: API.createShareDocuments(documents),
        onSuccess,
        messaging: {
          toast: {
            success: l10n.getString(
              "document-share-success",
              null,
              "Documents shared successfully",
            ),
            error: l10n.getString(
              "document-share-failure",
              null,
              "Failed to share documents. Please try again later.",
            ),
          },
        },
        ...overrideRunProps,
      });
    },
    [send, l10n],
  );

  return { createShareDocuments, loading };
};

type AccountDocumentsResponse = {
  documents: WebDocument[];
  meta?: { limit?: number };
};

type AccountDocumentsResults = {
  selectionKey: string;
  documents: WebDocument[];
  pagesLoaded: number;
  // The document store's page size, reported by the API.
  limit?: number;
  hasMore: boolean;
  hasError: boolean;
};

/**
 * Pages through documents for the selected accounts
 *
 * The first page probes for a second, and later pages load via `loadNextPage`.
 * Paging stops once a page adds no new documents, which also covers stores
 * that ignore offsets and return the same page again.
 */
export const useAccountDocuments = ({
  accountIds,
}: {
  accountIds: API.AccountId[];
}) => {
  const l10n = useLibrary("translations");
  const { send, loading: requestInFlight } =
    useRequestWithFeedback<AccountDocumentsResponse>();
  const [results, setResults] = useState<AccountDocumentsResults | null>(null);
  // Probe one page past the first, like the statements page does on load.
  const [pagesWanted, setPagesWanted] = useState(2);
  // Order-independent, so reselecting the same accounts reuses what's loaded.
  const selectionKey = [...accountIds].sort().join(",");
  const latestSelectionKey = useRef(selectionKey);
  latestSelectionKey.current = selectionKey;

  const loadedForSelection =
    results?.selectionKey === selectionKey ? results : null;
  const pagesLoaded = loadedForSelection?.pagesLoaded ?? 0;
  const limit = loadedForSelection?.limit;
  const needsPage =
    !!selectionKey &&
    (!loadedForSelection ||
      (loadedForSelection.hasMore && pagesLoaded < pagesWanted));

  useEffect(() => {
    setPagesWanted(2);
  }, [selectionKey]);

  // send() drops calls while a request is in flight, so fetch once it settles.
  // Responses for a selection the user has since changed are dropped.
  useEffect(() => {
    if (!needsPage || requestInFlight) {
      return;
    }
    // Zero-based offset. Stores such as FCM apply it to each account and
    // statement type separately, so advance by the store's page size rather
    // than the number of documents loaded.
    const after =
      pagesLoaded > 0 && limit !== undefined ? pagesLoaded * limit : undefined;
    send({
      action: API.fetchAccountDocuments(
        selectionKey.split(",") as API.AccountId[],
        after,
      ),
      onData: (data) => {
        if (latestSelectionKey.current !== selectionKey) return;
        const documents = data?.documents ?? [];
        setResults((previous) => {
          const existing =
            previous?.selectionKey === selectionKey ? previous.documents : [];
          const existingIds = new Set(existing.map((d) => d.id));
          const newDocuments = documents.filter((d) => !existingIds.has(d.id));
          return {
            selectionKey,
            documents: [...existing, ...newDocuments],
            pagesLoaded: pagesLoaded + 1,
            limit: data?.meta?.limit,
            hasMore: newDocuments.length > 0,
            hasError: false,
          };
        });
      },
      onError: () => {
        if (latestSelectionKey.current !== selectionKey) return;
        setResults((previous) => ({
          selectionKey,
          documents:
            previous?.selectionKey === selectionKey ? previous.documents : [],
          pagesLoaded,
          limit,
          hasMore: false,
          hasError: true,
        }));
      },
      messaging: {
        toast: {
          error: l10n.getString("document-fetch-failure", null),
        },
      },
    });
  }, [
    selectionKey,
    needsPage,
    requestInFlight,
    pagesLoaded,
    limit,
    send,
    l10n,
  ]);

  const loadNextPage = useCallback(() => {
    setPagesWanted(pagesLoaded + 1);
  }, [pagesLoaded]);

  return useObjectMemo({
    documents: loadedForSelection?.documents ?? [],
    isLoading: !!selectionKey && !loadedForSelection,
    hasMore: loadedForSelection?.hasMore ?? false,
    hasError: loadedForSelection?.hasError ?? false,
    loadNextPage,
  });
};
